<template>
  <v-container fluid class='ma-0 pa-0'>

    <!-- Dialog -->
    <v-dialog v-model="visible" :max-width="width" persistent>
      <v-card>
        <v-toolbar dark flat :color="toolbarColor" dense>
          <v-toolbar-title>
            <v-icon class="mr-2">edit</v-icon>{{ formTitle }}
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pa-0 pt-2 ma-0">
          <v-container grid-list-md class="pt-0 pb-0">
            <form>

              <!-- Name -->
              <v-row no-gutters>
                <v-col cols="12" sm="12">
                  <v-text-field
                    v-model="editedKiosk.name"
                    :label="getExpression('Label', 'Name')"
                    v-validate="'required|max:100'"
                    :error-messages="errors.collect('name')"
                    data-vv-name="name"
                    ref="name"
                    >
                  </v-text-field>
                </v-col>
              </v-row>

              <!-- Description -->
              <v-row no-gutters>
                <v-col cols="12" sm="12">
                  <v-text-field
                    v-model="editedKiosk.description"
                    :label="getExpression('Label', 'Description')"
                    >
                  </v-text-field>
                </v-col>
              </v-row>

              <!-- Language -->
              <template v-if="isAdmin || isDomainManager">
                <v-row no-gutters>
                  <v-col cols="12" sm="12">
                    <v-select
                      v-model="editedKiosk.languageId"
                      :items="languages"
                      item-value="id"
                      item-text="description"
                      :label="getExpression('Label','KioskLanguage')"
                      >
                    </v-select>
                  </v-col>
                </v-row>
              </template>

              <!-- Contact Info -->
              <template v-if="isGenie">
                <v-row no-gutters>
                  <v-col cols="12" sm="12">
                    <v-select
                      v-model="editedKiosk.contactUserId"
                      :items="contacts"
                      item-value="id"
                      item-text="name"
                      :label="getExpression('Label', 'KioskContact')"
                      >
                      <template v-slot:selection="{ item, index }">
                        <template v-if="item.id.length > 0">
                          <v-avatar size="36px" class="mr-2 mt-2">
                            <img :src="item.picture" />
                          </v-avatar>
                          {{ item.name }}
                        </template>
                        <template v-else>
                          {{ item.name }}
                        </template>
                      </template>

                      <template v-slot:item="{ item }">
                        <template v-if="item.id.length > 0">
                          <v-avatar size="36px" class="mr-2">
                            <img :src="item.picture" />
                          </v-avatar>
                          {{ item.name }}
                        </template>
                        <template v-else>
                          {{ item.name }}
                        </template>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>

                <v-row no-gutters>
                  <v-col cols="12" sm="12">
                    <vue-tel-input-vuetify
                      v-model="editedKiosk.contactPhone"
                      :label="getExpression('Label', 'ContactPhone')"
                      :onlyCountries="['CA', 'US']"
                      defaultCountry="CA"
                      :maxLen="20"
                      clearable>
                    </vue-tel-input-vuetify>
                  </v-col>
                </v-row>

                <v-row no-gutters>
                  <v-col cols="12" sm="12">
                    <v-text-field
                      v-model="editedKiosk.contactPosition"
                      :label="getExpression('Label', 'ContactPosition')"
                      clearable
                    />
                  </v-col>
                </v-row>
            
                <v-row no-gutters>
                  <v-col cols="12" sm="12">
                    <v-text-field
                      v-model="editedKiosk.contactAvailability"
                      :label="getExpression('Label', 'ContactAvailability')"
                      clearable
                    />
                  </v-col>
                </v-row>
              </template>
            </form>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn @click="cancel" :loading="isSaving" class="rounded-0" large>{{ getExpression('Button', 'Cancel') }}</v-btn>
          <v-btn :color="toolbarColorDark" dark @click="save" :loading="isSaving" class="rounded-0 ml-4" large>{{ getExpression('Button', 'Save') }}</v-btn>

        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import appService from '@/app.service.js'
import { mapGetters } from 'vuex'
import AppKiosk from '@/plugins/kiosk.js'
import { AppColor, KioskTypeId, LanguageId } from '@/config.js'

export default {
  name: 'AppKiosk',
  $_veeValidate: {
    validator: 'new'
  },
  props: {
    contactUsers: {
      type: Array,
      default: null
    }
  },
  data() {
    return {

      // colors
      appColor_Abby: AppColor.KioskTypeAbby,
      appColor_AbbyDark: AppColor.KioskTypeAbbyDark,
      appColor_GenieLight: AppColor.KioskTypeGenieLight,
      appColor_Genie: AppColor.KioskTypeGenie,

      // kiosk types
      kioskTypeId_Abby: KioskTypeId.Abby,
      kioskTypeId_Genie: KioskTypeId.Genie,

      isSaving: false,
      visible: false,
      onSave: {},
      onCancel: {},
      contacts: [],

      restoreKiosk: {
        id: -1,
        name: null,
        description: null,
        languageId: null,
        contactUserId: '',
        contactName: null,
        contactEmail: null,
        contactPhone: null,
        contactPosition: null,
        contactAvailability: null
      },
      editedKiosk: {
        id: -1,
        name: null,
        description: null,
        languageId: null,
        contactUserId: '',
        contactName: null,
        contactEmail: null,
        contactPhone: null,
        contactPosition: null,
        contactAvailability: null        
      }
    }
  },
  computed: {
    ...mapGetters([
      'profile',
      'languages',
      'activeKiosk',
      'activeResident',
      'isAdmin',
      'isDomainManager',
      'isKioskManager',
      'isResidentManager',
      'locale']),
    formTitle () {
      return this.editedKiosk.id > 0
        ? this.getExpression('FormTitle', 'EditKiosk')
        : this.getExpression('FormTitle', 'AddKiosk')
    },
    languagedLanguages () {
      const self = this
      if (this.isNullOrUndefined(this.languages)) return []

      return this.languages.map(l => { return {
        id: l.id,
        description: self.getExpression('Language', l.description)
      }})
    },
    toolbarColor () {
      return this.isAbby 
        ? this.appColor_Abby
        : this.appColor_GenieLight
    },
    toolbarColorDark () {
      return this.isAbby 
        ? this.appColor_AbbyDark
        : this.appColor_Genie
    },
    dictionary () { 
      return {
        custom: {
          name: {
            required: () => this.getExpression('Validation', 'KioskNameRequired'),
            max: this.getExpression('Validation', 'KioskNameMaxChars').replace('{0}', '200')
          },
          language: {
            required: () => this.getExpression('Validation', 'LanguageRequired')
          },
          contactPhone: {
            max: this.getExpression('Validation', 'ContactPhoneMaxChars').replace('{0}', '20')
          }
        }
      }
    },
    activeContact () {
      const userId = this.activeKiosk.contactUserId
      const contacts = this.contacts.filter(c => c.id === userId)
      return contacts.length > 0
        ? contacts[0]
        : null
    },
    isGenie () {
      if (this.isNullOrUndefined(this.activeKiosk)) return false
      return this.activeKiosk.typeId === this.kioskTypeId_Genie
    },
    isAbby () {
      if (this.isNullOrUndefined(this.activeKiosk)) return false
      return this.activeKiosk.typeId === this.kioskTypeId_Abby
    },
    width () {
      if (this.isNullOrUndefined(this.profile)) return 320
      return this.profile.languageId === LanguageId.English
        ? 320
        : 350
    }
  },
  methods: {
    add (params) {
      this.initParams(params)

      this.editedKiosk.languageId = !this.isNullOrUndefined(this.activeKiosk.languageId)
        ? this.activeKiosk.languageId
        : LanguageId.English

      this.visible = true
      this.setFocus()
    },

    edit (params) {
      this.initParams(params)

      this.editedKiosk.id = this.activeKiosk.id
      this.editedKiosk.name = this.activeKiosk.name
      this.editedKiosk.description = this.activeKiosk.description
      this.editedKiosk.languageId = this.activeKiosk.languageId
      this.editedKiosk.contactUserId = this.activeKiosk.contactUserId
      this.editedKiosk.contactPhone = this.activeKiosk.contactPhone
      this.editedKiosk.contactPosition = this.activeKiosk.contactPosition
      this.editedKiosk.contactAvailability = this.activeKiosk.contactAvailability

      this.restoreKiosk = Object.assign({}, this.editedKiosk)

      this.visible = true
      this.setFocus()
    },

    initParams (params) {
      if (this.isNullOrUndefined(params)) return
      if (!this.isNullOrUndefined(params.contactUsers)) this.contacts = params.contactUsers
      if (!this.isNullOrUndefined(params.onSave)) this.onSave = params.onSave
      if (!this.isNullOrUndefined(params.onCancel)) this.onCancel = params.onCancel
    },

    save () {
      const self = this
      self.$validator.resume()
      self.$validator.validateAll().then(valid => {
        if (valid) {
          self.isSaving = true
          self.update().then(data => {
            if (data.errorSeverity !== null) {
              self.handleError(data)
            } else {
              const name = data.kiosk.name
              const description = data.kiosk.description
              const languageId = data.kiosk.languageId
              const contactUserId = data.kiosk.contactUserId
              const contactPhone = data.kiosk.contactPhone
              const contactPosition = data.kiosk.contactPosition
              const contactAvailability = data.kiosk.contactAvailability

              self.$store.dispatch('updateKiosk', {
                kiosk: {
                  id: data.kiosk.id,
                  name: name,
                  description: description,
                  languageId: languageId,
                  contactUserId: contactUserId,
                  contactPhone: contactPhone,
                  contactPosition: contactPosition,
                  contactAvailability: contactAvailability,
                  hasResidents: self.activeKiosk.hasResidents
                }
              })

              self.reset()
              self.visible = false
              if (typeof this.onSave === 'function') {
                this.onSave(data)
              }
            }
            self.isSaving = false
          })
        } else {
          self.$validator.pause()
        }
      })
    },

    update () {
      return new Promise((resolve) => {
        const self = this
    
        const id = self.activeKiosk.id

        const contactUserId = self.editedKiosk.contactUserId !== null
        ? self.editedKiosk.contactUserId.length > 0
            ? self.editedKiosk.contactUserId
            : null
          : null
        const contactPhone = self.editedKiosk.contactPhone !== null
          ? self.editedKiosk.contactPhone.trim().length > 0
            ? self.editedKiosk.contactPhone.trim()
            : null
          : null
        const contactPosition = self.editedKiosk.contactPosition !== null
          ? self.editedKiosk.contactPosition.trim().length > 0
            ? self.editedKiosk.contactPosition.trim()
            : null
          : null
        const contactAvailability = self.editedKiosk.contactAvailability !== null
          ? self.editedKiosk.contactAvailability.trim().length > 0
            ? self.editedKiosk.contactAvailability.trim()
            : null
          : null

        const name = self.editedKiosk.name.trim()

        const description = self.editedKiosk.description !== null
          ? self.editedKiosk.description.trim().length > 0
            ? self.editedKiosk.description.trim()
            : null
          : null

        const languageId= self.editedKiosk.languageId

        const kioskModel = {
            name: name,
            description: description,
            languageId: languageId,
            contactUserId: contactUserId,
            contactPhone: contactPhone,
            contactPosition: contactPosition,
            contactAvailability: contactAvailability,
            updateByUserId: self.profile.id
          }

        if (name === self.activeKiosk.name && 
          description === self.activeKiosk.description &&
          languageId === self.activeKiosk.languageId &&
          contactUserId === self.activeKiosk.contactUserId &&
          contactPhone === self.activeKiosk.contactPhone &&
          contactPosition === self.activeKiosk.contactPosition &&
          contactAvailability === self.activeKiosk.contactAvailability) {
            
          kioskModel.id = id

          resolve({
            kiosk: kioskModel,
            errorSeverity: null
          })          
        } else {
          const baseOnly = true
          appService.updateKiosk(id, kioskModel, baseOnly, self.locale).then((data) => {
            resolve(data)
            self.isSaving = false
            self.editDialog = false
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    cancel () {
      const self = this

      if (typeof self.onCancel === 'function') {
        self.onCancel()
        self.visible = false
      } else {
        self.visible = false
      }
      setTimeout(() => {
        self.reset()
      }, 300)
    },

    reset () {
      this.editedKiosk = Object.assign({}, this.restoreKiosk)
      this.$validator.reset()
      this.$validator.pause()
    },

    setFocus () {
      const self = this
      setTimeout(() => {
        if (!this.isNullOrUndefined(self.$refs.name)) {
          self.$refs.name.focus()
        }
      }, 500)
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? this.getExpression('FormTitle', 'ValidationFailed')
          : this.getExpression('FormTitle', 'InternalError'),
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  beforeMount() {
    AppKiosk.EventBus.$on('add', (params) => {
      this.add(params)
    })
    AppKiosk.EventBus.$on('edit', (params) => {
      this.edit(params)
    })
  },
  mounted() {
    this.$validator.reset()
  }
}
</script>